import { type KeyboardEvent, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '../Input/Input';
import { DatePickerMenu } from './DatePickerMenu';
import { useAnchoredPanelPosition } from '../../hooks/useAnchoredPanelPosition';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { getOverlayPortalParent } from '../../utils/getOverlayPortalParent';
import './DatePicker.css';

const NUMERIC_DATE: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };

/** Date field (Figma "Input / Date"): a read-only input with a calendar icon that opens a `DatePickerMenu` below it. */
export type DatePickerInputProps = {
  label?: string;
  showLabel?: boolean;
  placeholder?: string;
  /** Selected date (controlled). */
  value?: Date | null;
  /** Called with the picked day; the calendar closes afterwards. */
  onChange?: (date: Date) => void;
  /** Earliest selectable date. */
  minDate?: Date;
  /** Latest selectable date. */
  maxDate?: Date;
  disabled?: boolean;
  /** Text shown for the selected date. Defaults to the browser locale's numeric date, e.g. 06/13/2026. */
  formatDate?: (date: Date) => string;
  id?: string;
  className?: string;
};

export function DatePickerInput({
  label = 'Select a date',
  showLabel = true,
  placeholder,
  value = null,
  onChange,
  minDate,
  maxDate,
  disabled = false,
  formatDate,
  id,
  className = '',
}: DatePickerInputProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  const panelStyle = useAnchoredPanelPosition(rootRef, panelRef, open);
  // The portaled calendar lives outside rootRef in the DOM.
  useOutsideClick([rootRef, panelRef], close, open);
  useEscapeKey(close, open);

  let text = '';
  if (value) text = formatDate ? formatDate(value) : value.toLocaleDateString(undefined, NUMERIC_DATE);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      if (!disabled) setOpen(true);
    }
  };

  const panel = (
    <div ref={panelRef} className="spark-date-picker-input-panel" style={panelStyle}>
      <DatePickerMenu
        value={value}
        minDate={minDate}
        maxDate={maxDate}
        showPreset={false}
        onChange={(date) => {
          onChange?.(date);
          close();
        }}
        onEscape={close}
      />
    </div>
  );

  return (
    <div ref={rootRef} className={['spark-date-picker-input', className].filter(Boolean).join(' ')}>
      <Input
        id={id}
        label={label}
        showLabel={showLabel}
        placeholder={placeholder}
        value={text}
        readOnly
        disabled={disabled}
        showPrefixIcon
        prefixIconSlug="calendar"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (!disabled) setOpen((current) => !current);
        }}
        onKeyDown={handleKeyDown}
      />
      {open ? createPortal(panel, getOverlayPortalParent(rootRef.current)) : null}
    </div>
  );
}
